'use client'

import { useState, useEffect, useRef } from 'react'

function isTabVisible() {
  return typeof document !== 'undefined' ? !document.hidden : true
}

export function useScoreboard(apiUrl, options = {}) {
  const {
    pollingInterval = 30000,
    enablePolling = false,
    fallbackData = []
  } = options

  const [matches, setMatches] = useState(fallbackData)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(null)

  const abortControllerRef = useRef(null)
  const pollingRef = useRef(null)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const fetchMatches = useRef(async (showLoading = true) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    try {
      if (showLoading) setLoading(true)
      setError(null)

      if (!apiUrl) {
        if (!mountedRef.current) return
        setMatches(fallbackData)
        setLoading(false)
        setLastUpdated(new Date())
        return
      }

      abortControllerRef.current = new AbortController()

      const response = await fetch(apiUrl, {
        cache: 'no-store',
        signal: abortControllerRef.current.signal,
        headers: {
          'Cache-Control': 'no-cache'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Error al carregar partits`)
      }

      const data = await response.json()

      if (!mountedRef.current) return

      const matchesData = Array.isArray(data)
        ? data
        : data.data || data.matches || []

      setMatches(matchesData)
      setLastUpdated(new Date())

      if (enablePolling) {
        console.log(
          '✅ Fetched matches:',
          matchesData.length,
          'at',
          new Date().toLocaleTimeString(),
          'and synced at',
          new Date(data.meta?.updatedAt).toLocaleTimeString() || 'N/A'
        )
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('Fetch aborted')
        return
      }

      console.error('Error fetching matches:', err)

      if (!mountedRef.current) return

      setError(err.message)
      if (matches.length === 0) {
        setMatches(fallbackData)
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false)
      }
    }
  }).current

  useEffect(() => {
    fetchMatches(true)

    if (!enablePolling) {
      // console.log('Polling disabled:', {
      //   enablePolling,
      //   isWeekend: isWeekend(),
      //   forceMode: process.env.FORCE_WEEKEND_MODE === 'true'
      // })
      return
    }

    console.log('✅ Polling ENABLED - every', pollingInterval, 'ms')

    const startPolling = () => {
      pollingRef.current = setTimeout(async () => {
        if (isTabVisible() && mountedRef.current) {
          console.log('🔄 Polling now...')
          await fetchMatches(false)
        }

        if (mountedRef.current) {
          startPolling()
        }
      }, pollingInterval)
    }

    startPolling()

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (pollingRef.current) {
          clearTimeout(pollingRef.current)
          pollingRef.current = null
          // console.log('⏸️ Polling paused (tab hidden)')
        }
      } else {
        // console.log('▶️ Polling resumed (tab visible)')
        fetchMatches(false)
        startPolling()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current)
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [apiUrl, pollingInterval, enablePolling, fetchMatches])

  return {
    matches,
    loading,
    error,
    lastUpdated,
    refetch: () => fetchMatches(false)
  }
}
